<template>
  <div class="mt-6">
    <div v-if="showHeader" class="mb-4">
      <!-- Compact Header -->
      <div class="flex justify-between items-center mb-4">
        <div>
          <h2 class="text-xl font-semibold text-gray-900">
            {{ isAllAgentsTable ? 'All Super Agents Report' : 'Single Agent Report' }}
          </h2>
          <p class="text-sm text-gray-600">
            {{ $route.params.from | moment("MMM Do, YYYY") }} - {{ $route.params.to | moment("MMM Do, YYYY") }}
          </p>
        </div>
        <button
          @click="handleDownload()"
          class="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
        >
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          Download
        </button>
      </div>
    </div>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <!-- All Agents Table Header -->
        <tr v-if="isAllAgentsTable">
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            AGENT
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            NO. TRANSACTIONS
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            SALES
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            CHAPMAN COMMISSION
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            AGENT COMMISSION
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            TOTAL COMMISSION
          </th>
        </tr>
        
        <!-- Single Agent Table Header -->
        <tr v-else>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            DATE
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            NO. TRANSACTIONS
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            SALES
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            CHAPMAN COMMISSION
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            AGENT COMMISSION
          </th>
          <th
            scope="col"
            class="px-6 py-3 text-xs font-medium text-right text-gray-500 uppercase tracking-wider"
          >
            TOTAL COMMISSION
          </th>
        </tr>
      </thead>

      <tbody class="bg-white divide-y divide-gray-200">
        <!-- All Agents Table Rows -->
        <template v-if="isAllAgentsTable">
          <tr v-for="(item, i) in data" :key="'agent-' + i">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">
                {{ item.agent }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <div class="text-sm text-gray-900">
                {{ item.transactions }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900">
                GMD <b>{{ formatPrice(item.sales) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.chapman_commission) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.agent_commission) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.total_commission) }}</b>
              </div>
            </td>
          </tr>
        </template>
        
        <!-- Single Agent Table Rows -->
        <template v-else>
          <tr v-for="(item, i) in data" :key="'date-' + i">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">
                {{ item.date | moment("MMM Do, YYYY") }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <div class="text-sm text-gray-900">
                {{ item.transactions }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900">
                GMD <b>{{ formatPrice(item.sales) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.chapman_commission) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.agent_commission) }}</b>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right">
              <div class="text-sm font-bold text-gray-900">
                GMD <b>{{ formatPrice(item.total_commission) }}</b>
              </div>
            </td>
          </tr>
        </template>

        <!-- Totals Row for All Agents Table -->
        <tr v-if="isAllAgentsTable" class="bg-gray-50">
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900">
              <b>TOTAL</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-center">
            <div class="text-sm text-gray-900">
              <b>{{ totalTransactions }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(totalSales) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(chapmanCommission) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(agentCommission) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 text-center whitespace-nowrap text-right">
            <div class="text-sm font-bold text-gray-900">
              GMD <b>{{ formatPrice(total) }}</b>
            </div>
          </td>
        </tr>
        
        <!-- Totals Row for Single Agent Table -->
        <tr v-else class="bg-gray-50">
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm font-medium text-gray-900">
              <b>TOTAL</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-center">
            <div class="text-sm text-gray-900">
              <b>{{ totalTransactions }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(totalSales) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(chapmanCommission) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 whitespace-nowrap text-right">
            <div class="text-sm text-gray-900">
              GMD <b>{{ formatPrice(agentCommission) }}</b>
            </div>
          </td>
          <td class="px-6 py-4 text-center whitespace-nowrap text-right">
            <div class="text-sm font-bold text-gray-900">
              GMD <b>{{ formatPrice(total) }}</b>
            </div>
          </td>
        </tr>

        <!-- More people... -->
      </tbody>
    </table>
    </div>
  </div>
</template>

<script>
export default {
  props: ["data", "showHeader"],

  data: () => ({
    downloadLoading: false,

    filename: "",
    autoWidth: true,
    bookType: "xlsx",
  }),

  computed: {
    isAllAgentsTable() {
      // Check if data contains 'agent' property (all agents table)
      // vs 'date' property (single agent table)
      return this.data && this.data.length > 0 && 'agent' in this.data[0];
    },

    totalTransactions: function () {
      if (!this.data || !Array.isArray(this.data)) return 0;
      
      return this.data.reduce((total, item) => {
        return total + (item.transactions || 0);
      }, 0);
    },

    totalSales: function () {
      if (!this.data || !Array.isArray(this.data)) return 0;
      
      return this.data.reduce((total, item) => {
        return total + (item.sales || 0);
      }, 0);
    },

    chapmanCommission: function () {
      if (!this.data || !Array.isArray(this.data)) return 0;
      
      return this.data.reduce((total, item) => {
        return total + (item.chapman_commission || 0);
      }, 0);
    },

    agentCommission: function () {
      if (!this.data || !Array.isArray(this.data)) return 0;
      
      return this.data.reduce((total, item) => {
        return total + (item.agent_commission || 0);
      }, 0);
    },

    total: function () {
      if (!this.data || !Array.isArray(this.data)) return 0;
      
      return this.data.reduce((total, item) => {
        return total + (item.total_commission || 0);
      }, 0);
    },
  },

  methods: {
    formatPrice(value) {
      let val = (value / 1).toFixed(2).replace(",", ".");
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getFileName() {
      return (
        this.$route.params.reportOn +
        "-" +
        this.$route.params.from +
        "-" +
        this.$route.params.to
      );
    },

    handleDownload() {
      this.downloadLoading = true;
      import("@/vendor/Export2Excel").then((excel) => {
        let tHeader, filterVal, data;
        
        if (this.isAllAgentsTable) {
          // All Agents Table
          tHeader = [
            "Agent",
            "Transactions",
            "Sales",
            "Chapman Commission",
            "Agent Commission",
            "Total Commission",
          ];
          filterVal = [
            "agent",
            "transactions",
            "sales",
            "chapman_commission",
            "agent_commission",
            "total_commission",
          ];
        } else {
          // Single Agent Table
          tHeader = [
            "Date",
            "Transactions",
            "Sales",
            "Chapman Commission",
            "Agent Commission",
            "Total Commission",
          ];
          filterVal = [
            "date",
            "transactions",
            "sales",
            "chapman_commission",
            "agent_commission",
            "total_commission",
          ];
        }
        
        data = this.formatJson(filterVal, this.data);
        excel.export_json_to_excel({
          header: tHeader,
          data,
          filename: this.getFileName(),
          autoWidth: this.autoWidth,
          bookType: this.bookType,
        });
        this.downloadLoading = false;
      });
    },

    formatJson(filterVal, jsonData) {
      if (!Array.isArray(jsonData)) return [];
      
      return jsonData.map((item) => {
        return filterVal.map((field) => {
          return item[field] || '';
        });
      });
    },
  },
};
</script>
